import {
  Badge,
  Button,
  Card,
  Field,
  Input,
  MicroLabel,
  Reveal,
  SectionRule,
  Stat,
  StatGrid,
} from '@web/components/ui';
import {
  CheckCircle2,
  CreditCard,
  Fingerprint,
  MousePointerClick,
  PlayCircle,
  Radio,
  ShoppingCart,
  Sparkles,
  Tag,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, Route, Routes, useLocation } from 'react-router-dom';

import { DebugConsole } from './components/DebugConsole';
import type { VestigoUserData } from './global';
import { consent, getVisitorId, identify, onReady, track } from './vestigo';

/** Eventos padrão do Vestigo que mapeiam para os destinos (Meta/GA4/Ads). */
const STANDARD_EVENTS: Array<{ name: string; params?: Record<string, unknown> }> = [
  { name: 'ViewContent', params: { content_name: 'Plano Pro', content_ids: ['pro'], value: 99.9, currency: 'BRL' } },
  { name: 'Search', params: { search_string: 'atribuição multicanal' } },
  { name: 'AddToCart', params: { content_ids: ['pro'], value: 99.9, currency: 'BRL' } },
  { name: 'InitiateCheckout', params: { num_items: 1, value: 99.9, currency: 'BRL' } },
  { name: 'AddPaymentInfo', params: { payment_type: 'pix' } },
  { name: 'Subscribe', params: { plan: 'pro', value: 99.9, currency: 'BRL' } },
  { name: 'CompleteRegistration', params: { method: 'email' } },
  { name: 'Contact', params: { channel: 'whatsapp' } },
  { name: 'Lead', params: { form: 'demo', value: 50, currency: 'BRL' } },
];

const PURCHASE_PARAMS = {
  value: 199.9,
  currency: 'BRL',
  order_id: 'A' + '1234',
  content_ids: ['pro', 'addon'],
  num_items: 2,
};

function Section({
  id,
  icon,
  kicker,
  title,
  children,
}: {
  id: string;
  icon: ReactNode;
  kicker: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="mx-auto max-w-4xl scroll-mt-24 px-6 py-14">
      <Reveal>
        <SectionRule>
          <span className="inline-flex items-center gap-2">
            {icon}
            {kicker}
          </span>
        </SectionRule>
        <h2 className="mb-6 font-display text-2xl font-semibold uppercase tracking-tight md:text-3xl">
          {title}
        </h2>
        {children}
      </Reveal>
    </section>
  );
}

function Header() {
  return (
    <header className="sticky top-0 z-40 border-b border-dark/10 bg-surface/90 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-4xl items-center justify-between px-6">
        <Link to="/" className="flex items-center gap-3">
          <img src="/logo-vestigo.png" alt="Vestigo" className="h-6 w-auto" />
          <Badge tone="info">LP de Teste</Badge>
        </Link>
        <nav className="flex items-center gap-6 text-[10px] font-semibold uppercase tracking-[0.25em] text-dark/50">
          <Link to="/" className="hover:text-accent">
            Início
          </Link>
          <Link to="/planos" className="hover:text-accent">
            Planos
          </Link>
          <Link to="/obrigado" className="hover:text-accent">
            Obrigado
          </Link>
        </nav>
      </div>
    </header>
  );
}

function Hero() {
  const [vid, setVid] = useState<string | null>(getVisitorId());
  useEffect(() => onReady(setVid), []);

  // Para qual propriedade a LP envia (vem do /t/lab-config em runtime). Evita a
  // confusão de procurar os eventos na propriedade errada no painel. Quando não
  // há propriedade, o servidor manda o `motivo` — e ele aparece aqui, porque a
  // alternativa é a que existia antes: uma página que carrega, não dispara nada
  // e não diz por quê.
  const [target, setTarget] = useState<{
    clientName?: string;
    propertyName?: string;
    publicKey?: string;
    motivo?: string | null;
  } | null>(null);
  useEffect(() => {
    let tries = 0;
    const read = () => {
      const lab = (window as unknown as { __vstLab?: typeof target }).__vstLab;
      if (lab?.publicKey || lab?.motivo) {
        setTarget(lab);
        return true;
      }
      return false;
    };
    if (read()) return;
    const t = window.setInterval(() => {
      tries += 1;
      if (read() || tries > 30) window.clearInterval(t);
    }, 150);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="relative overflow-hidden border-b border-dark/10">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(to right, #2d322f 1px, transparent 1px), linear-gradient(to bottom, #2d322f 1px, transparent 1px)',
          backgroundSize: '64px 64px',
        }}
      />
      <div className="relative mx-auto max-w-4xl px-6 py-20 text-center">
        <Reveal>
          <span className="mb-5 inline-block bg-accent px-3 py-1.5 text-[9px] font-semibold uppercase tracking-[0.35em] text-white">
            Banco de testes de tracking
          </span>
          <h1 className="mx-auto max-w-2xl font-display text-4xl font-semibold uppercase leading-none tracking-tighter md:text-6xl">
            Dispare todos os eventos do Vestigo
          </h1>
          <p className="mx-auto mt-5 max-w-xl font-accent text-lg italic text-dark/60">
            Onde cada configuração encontra sua verificação.
          </p>
          <div className="mx-auto mt-8 max-w-md border border-dark/15 bg-muted px-4 py-3 font-mono text-xs text-dark/60">
            visitorId:{' '}
            <span className="text-accent">{vid ?? 'carregando snippet…'}</span>
          </div>
          {target?.publicKey && (
            <div className="mx-auto mt-3 max-w-md border border-accent/25 bg-accent/[0.06] px-4 py-3 text-xs">
              <span className="text-dark/50">Os eventos vão para a propriedade:</span>{' '}
              <span className="font-semibold text-accent">
                {target.clientName} ▸ {target.propertyName}
              </span>
              <div className="mt-0.5 font-mono text-[11px] text-dark/40">{target.publicKey}</div>
            </div>
          )}
          {/*
            Ocre, e não ferrugem: falta configurar, não deu erro. É a mesma
            leitura de estado do painel — pendência é `warn`, e quem lê o
            produto já sabe disso sem aprender nada novo.
          */}
          {target && !target.publicKey && target.motivo && (
            <div className="mx-auto mt-3 max-w-md border border-warn/30 bg-warn/[0.07] px-4 py-3 text-left text-xs">
              <div className="text-[9px] font-semibold uppercase tracking-[0.2em] text-warn">
                Laboratório sem propriedade
              </div>
              <p className="mt-1.5 leading-relaxed text-dark/60">{target.motivo}</p>
            </div>
          )}
          <p className="mt-4 text-xs text-dark/40">
            Abra o console de debug (canto inferior direito) e role a página.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function ConsentSection() {
  const [state, setState] = useState<'unset' | 'granted' | 'denied'>('unset');
  return (
    <Section
      id="consent"
      icon={<CheckCircle2 size={13} />}
      kicker="Consentimento (LGPD)"
      title="Consent — granted / denied"
    >
      <Card>
        <p className="mb-4 text-sm leading-relaxed text-dark/60">
          Se a propriedade exigir consentimento em modo <b>block</b>, nenhum evento persiste — nem o
          PageView — até você aceitar. Em modo <b>signal</b>, os eventos fluem marcados como negados.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={() => {
              consent(true);
              setState('granted');
            }}
          >
            Aceitar cookies
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              consent(false);
              setState('denied');
            }}
          >
            Recusar
          </Button>
          {state !== 'unset' && (
            <Badge tone={state === 'granted' ? 'ok' : 'warn'}>
              consent = {state === 'granted' ? 'true' : 'false'}
            </Badge>
          )}
        </div>
      </Card>
    </Section>
  );
}

function StandardEventsSection() {
  return (
    <Section
      id="eventos"
      icon={<ShoppingCart size={13} />}
      kicker="track() manual"
      title="Eventos padrão + commerce"
    >
      <div className="grid grid-cols-2 gap-px bg-dark/10 sm:grid-cols-3">
        {STANDARD_EVENTS.map((e) => (
          <button
            key={e.name}
            onClick={() => track(e.name, e.params)}
            className="bg-surface px-4 py-5 text-center text-[11px] font-semibold uppercase tracking-widest text-dark transition-colors hover:bg-accent hover:text-white"
          >
            {e.name}
          </button>
        ))}
        <button
          onClick={() => track('Purchase', PURCHASE_PARAMS)}
          className="bg-dark px-4 py-5 text-center text-[11px] font-semibold uppercase tracking-widest text-white transition-colors hover:bg-accent"
        >
          Purchase · R$199,90
        </button>
        <button
          onClick={() => track('QuizCompleted', { score: 8, segment: 'quente' })}
          className="bg-surface px-4 py-5 text-center text-[11px] font-semibold uppercase tracking-widest text-dark transition-colors hover:bg-accent hover:text-white"
        >
          Evento custom
        </button>
      </div>
      <p className="mt-4 text-xs text-dark/40">
        Purchase carrega <code className="font-mono text-accent">value / currency / order_id</code>{' '}
        — os campos que os destinos (Meta CAPI, GA4, Google Ads) consomem.
      </p>
    </Section>
  );
}

function DeclarativeSection() {
  return (
    <Section
      id="declarativos"
      icon={<MousePointerClick size={13} />}
      kicker="Sem código — data-vestigo-*"
      title="Captura declarativa por clique"
    >
      <p className="mb-4 text-sm leading-relaxed text-dark/60">
        Estes botões <b>não</b> têm handler JS. O snippet captura o clique via os atributos{' '}
        <code className="font-mono text-accent">data-vestigo-event</code> e transforma os demais{' '}
        <code className="font-mono text-accent">data-vestigo-*</code> em params (kebab → snake_case).
      </p>
      <div className="flex flex-wrap gap-3">
        <button
          data-vestigo-event="AddToCart"
          data-vestigo-plan-id="pro"
          data-vestigo-price="99.9"
          className="border border-dark/20 px-6 py-3 text-[11px] font-semibold uppercase tracking-widest hover:border-accent hover:bg-accent hover:text-white"
        >
          Assinar Pro (declarativo)
        </button>
        <button
          data-vestigo-event="Contact"
          data-vestigo-channel="whatsapp"
          className="border border-dark/20 px-6 py-3 text-[11px] font-semibold uppercase tracking-widest hover:border-accent hover:bg-accent hover:text-white"
        >
          Falar no WhatsApp
        </button>
        <a
          href="#declarativos"
          data-vestigo-event="Download"
          data-vestigo-asset="ebook-atribuicao"
          className="border border-dark/20 px-6 py-3 text-[11px] font-semibold uppercase tracking-widest hover:border-accent hover:bg-accent hover:text-white"
        >
          Baixar e-book
        </a>
      </div>
    </Section>
  );
}

function IdentifySection() {
  const [form, setForm] = useState<VestigoUserData>({});
  const [sent, setSent] = useState(false);
  const set = (k: keyof VestigoUserData) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Section
      id="identify"
      icon={<Fingerprint size={13} />}
      kicker="identify()"
      title="Identidade do visitante"
    >
      <Card>
        <p className="mb-5 text-sm leading-relaxed text-dark/60">
          <code className="font-mono text-accent">identify()</code> não envia nada sozinho — funde
          os dados em todo evento seguinte. O PII é hasheado no worker antes do fan-out.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="E-mail">
            <Input type="email" placeholder="cliente@exemplo.com" onChange={set('email')} />
          </Field>
          <Field label="Telefone">
            <Input placeholder="+55 11 90000-0000" onChange={set('phone')} />
          </Field>
          <Field label="Nome">
            <Input onChange={set('firstName')} />
          </Field>
          <Field label="Sobrenome">
            <Input onChange={set('lastName')} />
          </Field>
          <Field label="Cidade">
            <Input onChange={set('city')} />
          </Field>
          <Field label="External ID">
            <Input placeholder="user_123" onChange={set('externalId')} />
          </Field>
        </div>
        <div className="mt-5 flex items-center gap-3">
          <Button
            onClick={() => {
              identify(form);
              setSent(true);
            }}
          >
            Enviar identify
          </Button>
          {sent && <Badge tone="ok">dados fundidos</Badge>}
        </div>
      </Card>
    </Section>
  );
}

function LeadFormSection() {
  const [ok, setOk] = useState(false);
  return (
    <Section id="lead" icon={<Tag size={13} />} kicker="submit + track" title="Formulário de Lead">
      <Card>
        <form
          id="form-lead"
          onSubmit={(e) => {
            e.preventDefault();
            const data = new FormData(e.currentTarget);
            identify({ email: String(data.get('email') ?? '') });
            track('Lead', { form: 'contato', value: 50, currency: 'BRL' });
            setOk(true);
          }}
          className="grid gap-4 sm:grid-cols-2"
        >
          <Field label="Nome">
            <Input name="nome" required />
          </Field>
          <Field label="E-mail">
            <Input name="email" type="email" required />
          </Field>
          <div className="sm:col-span-2 flex items-center gap-3">
            <Button type="submit">Quero uma demo</Button>
            {ok && <Badge tone="ok">Lead enviado</Badge>}
          </div>
        </form>
        <p className="mt-4 text-xs text-dark/40">
          Também serve de alvo para um remote trigger <b>submit</b> em <code className="font-mono">#form-lead</code>.
        </p>
      </Card>
    </Section>
  );
}

function MediaSection() {
  return (
    <Section
      id="midia"
      icon={<PlayCircle size={13} />}
      kicker="autoCapture.video (config no painel)"
      title="Vídeo — start / complete"
    >
      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <MicroLabel className="mb-2 block">HTML5 &lt;video&gt;</MicroLabel>
          <video
            controls
            className="w-full border border-dark/10 bg-deep"
            src="https://www.w3schools.com/html/mov_bbb.mp4"
          />
        </div>
        <div>
          <MicroLabel className="mb-2 block">YouTube (postMessage)</MicroLabel>
          <div className="aspect-video border border-dark/10">
            <iframe
              className="h-full w-full"
              src="https://www.youtube.com/embed/aqz-KE-bpKQ?enablejsapi=1"
              title="teste"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
            />
          </div>
        </div>
      </div>
      <p className="mt-4 text-xs text-dark/40">
        Só dispara <code className="font-mono">video_start</code>/<code className="font-mono">video_complete</code>{' '}
        se <code className="font-mono text-accent">autoCapture.video</code> estiver ligado na propriedade.
      </p>
    </Section>
  );
}

function CheckoutSection() {
  // O link aponta para o 1º domínio de checkout configurado na propriedade
  // (vindo de /t/lab-config em runtime) — assim a decoração ?vid= sempre casa.
  // Fallback para o domínio demo, que já vem cadastrado na propriedade do lab.
  const [host, setHost] = useState('checkout.vestigo-teste.com.br');
  useEffect(() => {
    let tries = 0;
    const read = () => {
      const lab = (window as unknown as { __vstLab?: { checkoutDomains?: string[] } }).__vstLab;
      if (lab?.checkoutDomains && lab.checkoutDomains.length > 0) {
        setHost(lab.checkoutDomains[0]!);
        return true;
      }
      return false;
    };
    if (read()) return;
    const t = window.setInterval(() => {
      tries += 1;
      if (read() || tries > 20) window.clearInterval(t);
    }, 150);
    return () => window.clearInterval(t);
  }, []);

  return (
    <Section
      id="checkout"
      icon={<CreditCard size={13} />}
      kicker="checkoutDomains (config no painel)"
      title="Decoração de link de checkout"
    >
      <Card>
        <p className="mb-4 text-sm leading-relaxed text-dark/60">
          O link aponta para <code className="font-mono text-accent">{host}</code>. Como esse domínio
          está em <code className="font-mono text-accent">checkoutDomains</code> da propriedade, o
          snippet anexa <code className="font-mono text-accent">?vid=&lt;visitorId&gt;</code> ao clicar —
          costurando a identidade cross-domain. Verifique a URL de destino.
        </p>
        <a
          href={`https://${host}/pro`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 bg-dark px-6 py-3 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-accent"
        >
          <CreditCard size={14} /> Ir para o checkout ({host})
        </a>
      </Card>
    </Section>
  );
}

function TriggersSection() {
  const [visible, setVisible] = useState(false);
  return (
    <Section
      id="triggers"
      icon={<Radio size={13} />}
      kicker="Remote triggers (config no painel)"
      title="Alvos para triggers click / visible / route"
    >
      <p className="mb-4 text-sm leading-relaxed text-dark/60">
        Crie triggers no painel sem tocar no código. Estes elementos têm seletores estáveis; a
        navegação entre rotas (topo) exercita o trigger <b>route</b>.
      </p>
      <div className="flex flex-wrap gap-3">
        <button id="cta-hero" className="cta-tracked border border-dark/20 px-6 py-3 text-[11px] font-semibold uppercase tracking-widest hover:border-accent hover:bg-accent hover:text-white">
          #cta-hero (trigger click)
        </button>
        <button
          onClick={() => setVisible(true)}
          className="border border-dark/20 px-6 py-3 text-[11px] font-semibold uppercase tracking-widest hover:border-accent hover:bg-accent hover:text-white"
        >
          Revelar alvo visível
        </button>
      </div>
      {visible && (
        <div
          id="trigger-visible-alvo"
          className="mt-6 border border-accent/30 bg-accent/5 px-6 py-10 text-center text-sm text-accent"
        >
          #trigger-visible-alvo — alvo para trigger <b>visible</b> (IntersectionObserver)
        </div>
      )}
    </Section>
  );
}

function AttributionSection() {
  const withUtms = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('utm_source', 'teste');
    url.searchParams.set('utm_medium', 'lp');
    url.searchParams.set('utm_campaign', 'bench');
    /*
     * Click-ids de teste no formato REAL — base64url, sem ponto.
     *
     * `FB.abc123` custou 144 eventos com `fbc` malformado no Events Manager: o
     * snippet monta `fb.<índice>.<ms>.<fbclid>`, o ponto do falso `fbclid` cria
     * um quinto campo, e o `_fbc` resultante fica um ANO no cookie de
     * `.vestigo.ia.br` — contaminando o blog e tudo mais que o mesmo navegador
     * visitar. Página de teste escreve no mesmo domínio que a produção; o dado
     * falso precisa ter a forma do verdadeiro.
     */
    url.searchParams.set('fbclid', 'IwAR0TesteVestigoLp1234567890');
    url.searchParams.set('gclid', 'Cj0KCQjwTesteVestigoLp');
    window.location.href = url.toString();
  };
  return (
    <Section
      id="atribuicao"
      icon={<Tag size={13} />}
      kicker="UTMs + click-ids (automático)"
      title="Atribuição"
    >
      <Card>
        <p className="mb-4 text-sm leading-relaxed text-dark/60">
          O snippet captura UTMs e click-ids (<code className="font-mono">fbclid, gclid, wbraid,
          gbraid, msclkid, ctwa_clid</code>) da URL e os injeta em todo evento. Recarregue com
          parâmetros e dispare um evento para conferir.
        </p>
        <Button variant="outline" onClick={withUtms}>
          Recarregar com UTMs + click-ids
        </Button>
      </Card>
    </Section>
  );
}

function TestBench() {
  return (
    <>
      <Hero />
      <div className="mx-auto max-w-4xl px-6 pt-12">
        <StatGrid columns={4}>
          <Stat label="Eventos padrão" value="11" />
          <Stat label="Automáticos" value="5" tone="ok" />
          <Stat label="Configs painel" value="4" tone="info" />
          <Stat label="Formulários" value="2" />
        </StatGrid>
      </div>
      <ConsentSection />
      <StandardEventsSection />
      <DeclarativeSection />
      <IdentifySection />
      <LeadFormSection />
      <MediaSection />
      <CheckoutSection />
      <TriggersSection />
      <RevelacaoSection />
      <AttributionSection />
    </>
  );
}

/**
 * ── O ALVO DA REVELAÇÃO DE DOBRA ────────────────────────────────────────────
 *
 * A revelação do V-Pop **não injeta conteúdo**: ela anima um trecho que já é da
 * página do cliente. Isso a torna a peça mais difícil de conferir de todas — se
 * o seletor casar um título que já estava na tela, não há nada para revelar, e
 * quem olha conclui que o recurso não funciona.
 *
 * Foi o que aconteceu em 15/08/2026, com o seletor apontado para `h2`: os
 * títulos acima da dobra já estavam visíveis, e o motor tem uma rede de
 * segurança que os revela na hora, sem animação.
 *
 * Este bloco existe para dar à revelação um alvo que **não deixa dúvida**:
 * grande, com muito ar em volta, no fim de uma página longa — de modo que ele
 * só entre na tela por rolagem, e o movimento seja inequívoco. O seletor a
 * apontar é `#alvo-da-revelacao`.
 *
 * Ele fica sem estilo de entrada próprio de propósito: se aparecer animando,
 * o movimento é do V-Pop. Se aparecer parado, o V-Pop não fez o trabalho dele.
 */
function RevelacaoSection() {
  return (
    <Section
      id="revelacao"
      icon={<Sparkles size={13} />}
      kicker="Alvo da revelação de dobra"
      title="Este bloco existe para ser revelado"
    >
      <p className="mb-4 text-sm leading-relaxed text-dark/60">
        A revelação anima um trecho que <b>já é da página</b> — ela nunca injeta texto novo. Aponte
        o seletor da peça para <code className="font-mono text-[12px]">#alvo-da-revelacao</code> e
        role até aqui: o bloco abaixo deve <b>subir ao entrar na tela</b>. Se ele já estiver parado
        no lugar quando chegar, a revelação não fez nada.
      </p>
      <div
        id="alvo-da-revelacao"
        className="border-2 border-accent/40 bg-accent/5 px-8 py-24 text-center"
      >
        <div className="text-[11px] font-semibold uppercase tracking-widest text-accent/70">
          #alvo-da-revelacao
        </div>
        <div className="mt-4 text-4xl font-semibold leading-tight text-dark">
          Se este bloco subiu ao entrar na tela,
          <br />a revelação de dobra funcionou.
        </div>
        <div className="mt-4 text-sm text-dark/50">
          Se ele apareceu parado, sem movimento, ela não funcionou — e isso é o defeito.
        </div>
      </div>
    </Section>
  );
}

function SimplePage({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mx-auto max-w-4xl px-6 py-24">
      <Reveal>
        <MicroLabel className="mb-3 block">Rota de teste</MicroLabel>
        <h1 className="font-display text-4xl font-semibold uppercase tracking-tighter">{title}</h1>
        <div className="mt-6 text-sm leading-relaxed text-dark/60">{children}</div>
        <div className="mt-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-dark px-6 py-3 text-[11px] font-semibold uppercase tracking-widest text-white hover:bg-accent"
          >
            ← Voltar ao banco de testes
          </Link>
        </div>
      </Reveal>
    </div>
  );
}

export default function App() {
  const location = useLocation();

  // Cada mudança de rota é um alvo natural do trigger `route` do snippet
  // (que faz hook em pushState). Aqui só disparamos um PageView manual extra
  // para deixar a navegação SPA visível na timeline.
  useEffect(() => {
    if (location.key !== 'default') track('PageView', { path: location.pathname, spa: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <div className="min-h-screen pb-24">
      <Header />
      <Routes>
        <Route path="/" element={<TestBench />} />
        <Route
          path="/planos"
          element={
            <SimplePage title="Planos">
              Rota secundária para exercitar o trigger <b>route</b> (SPA pushState) e a navegação.
            </SimplePage>
          }
        />
        <Route
          path="/obrigado"
          element={
            <SimplePage title="Obrigado!">
              Página de agradecimento — bom alvo para confirmar uma conversão após o checkout.
            </SimplePage>
          }
        />
        <Route path="*" element={<SimplePage title="404">Rota não encontrada.</SimplePage>} />
      </Routes>
      <DebugConsole />
    </div>
  );
}
